(() => {
  if (globalThis.kloveStoryBannersInstalled) return;
  globalThis.kloveStoryBannersInstalled = true;
  const init = () => document.querySelectorAll('[data-story-banners]').forEach(section => {
    if (section.dataset.initialized) return;
    section.dataset.initialized = 'true';
    const track = section.querySelector('[data-story-track]');
    const slides = [...section.querySelectorAll('[data-story-slide]')];
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    if (!track || slides.length < 2) return;
    const count = section.querySelector('[data-story-count]');
    const pagination = section.querySelector('[data-story-pagination]');
    const interval = Math.max(4000, Number(section.dataset.interval) || 6000);
    const controller = new AbortController();
    const listen = (target, type, callback, options = {}) => target.addEventListener(type, callback, { ...options, signal: controller.signal });
    let paused = section.dataset.autoplay === 'false' || reduced.matches;
    let hovered = false;
    let focused = false;
    let touching = false;
    let visible = true;
    let timer;
    let progressAnimation;
    let current = 0;
    const schedule = () => {
      clearTimeout(timer);
      progressAnimation?.cancel();
      progressAnimation = null;
      if (!controller.signal.aborted && !paused && !hovered && !focused && !touching && visible && !document.hidden && section.isConnected) {
        const fill = dots[current]?.querySelector('.klove-stories__progress');
        progressAnimation = fill?.animate?.([{transform:'scaleX(0)'}, {transform:'scaleX(1)'}], {duration:interval, easing:'linear', fill:'forwards'});
        timer = setTimeout(() => go(current + 1), interval);
      }
    };
    const takeControl = () => {
      paused = true;
      schedule();
    };
    const go = (index, behavior = reduced.matches ? 'instant' : 'smooth') => {
      const next = (index + slides.length) % slides.length;
      track.scrollTo({left:next * track.clientWidth, behavior});
    };
    // Build from rendered slides, so hidden/empty theme blocks never add a dot.
    const dots = pagination ? slides.map((slide, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'klove-stories__dot';
      const label = slide.querySelector('img')?.alt || slide.getAttribute('aria-label') || '';
      button.setAttribute('aria-label', `${index + 1} / ${slides.length}${label ? ` — ${label}` : ''}`);
      const marker = document.createElement('span');
      marker.className = 'klove-stories__marker';
      marker.setAttribute('aria-hidden', 'true');
      const fill = document.createElement('span');
      fill.className = 'klove-stories__progress';
      marker.append(fill);
      button.append(marker);
      listen(button, 'click', () => { takeControl(); go(index); });
      return button;
    }) : [];
    const updatePagination = () => dots.forEach((dot, index) => {
      if (index === current) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    if (pagination) {
      pagination.replaceChildren(...dots);
      pagination.hidden = false;
      updatePagination();
    }
    listen(track, 'scroll', () => {
      // Safari can report scrollLeft beyond either edge during rubber-band scrolling.
      const next = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / (track.clientWidth || 1))));
      if (next === current) return;
      current = next;
      updatePagination();
      if (count) count.textContent = `${current + 1} / ${slides.length}`;
      schedule();
    }, {passive:true});
    let drag = null;
    let suppressClick = false;
    listen(track, 'dragstart', event => event.preventDefault());
    listen(track, 'pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      suppressClick = false;
      drag = {x:event.clientX, left:track.scrollLeft, moved:false};
    });
    listen(track, 'pointermove', event => {
      if (!drag) return;
      const delta = event.clientX - drag.x;
      if (Math.abs(delta) > 8 && !drag.moved) {
        drag.moved = true;
        track.setPointerCapture(event.pointerId);
        track.style.scrollSnapType = 'none';
      }
      if (drag.moved) track.scrollLeft = drag.left - delta;
    });
    const endDrag = () => {
      if (!drag) return;
      suppressClick = drag.moved;
      drag = null;
      track.style.scrollSnapType = '';
      if (suppressClick) go(Math.round(track.scrollLeft / track.clientWidth));
    };
    listen(track, 'pointerup', endDrag);
    listen(track, 'pointercancel', endDrag);
    listen(track, 'pointerleave', () => { if (drag && !drag.moved) drag = null; });
    listen(track, 'click', event => {
      if (suppressClick) { event.preventDefault(); suppressClick = false; }
    }, true);
    listen(track, 'keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      takeControl();
      const next = (current + (event.key === 'ArrowRight' ? 1 : -1) + slides.length) % slides.length;
      go(next);
      if (event.target !== track) {
        const destination = slides[next].matches('a[href]') ? slides[next] : track;
        destination.focus({preventScroll:true});
      }
    });
    listen(section, 'pointerenter', event => { if (event.pointerType === 'mouse') { hovered = true; schedule(); } });
    listen(section, 'pointerleave', () => { hovered = false; schedule(); });
    listen(section, 'focusin', () => { focused = true; schedule(); });
    listen(section, 'focusout', event => { focused = section.contains(event.relatedTarget); schedule(); });
    listen(track, 'pointerdown', () => { touching = true; takeControl(); });
    listen(track, 'wheel', event => { if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) takeControl(); }, {passive:true});
    listen(section, 'keydown', event => { if (event.key === 'Escape') takeControl(); });
    const release = () => { touching = false; schedule(); };
    listen(window, 'pointerup', release, {signal: controller.signal});
    listen(window, 'pointercancel', release, {signal: controller.signal});
    listen(document, 'visibilitychange', schedule, {signal: controller.signal});
    listen(reduced, 'change', () => { if (reduced.matches) paused = true; schedule(); }, {signal: controller.signal});
    const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => { visible = entries[0].isIntersecting; schedule(); }) : null;
    observer?.observe(section);
    // Keep the same photograph after rotating a phone or resizing the viewport.
    let trackWidth = track.clientWidth;
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(() => {
      if (!track.clientWidth || track.clientWidth === trackWidth) return;
      trackWidth = track.clientWidth;
      go(current, 'instant');
      schedule();
    }) : null;
    resize?.observe(track);
    listen(document, 'shopify:section:unload', event => {
      if (!event.target.contains(section)) return;
      clearTimeout(timer);
      observer?.disconnect();
      resize?.disconnect();
      progressAnimation?.cancel();
      controller.abort();
      delete section.dataset.initialized;
    }, {signal: controller.signal});
    schedule();
  });
  init();
  document.addEventListener('shopify:section:load', init);
})();
